import type { CSSProperties } from 'react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Markdown component - renders markdown-formatted text with GFM support.
 *
 * Uses react-markdown with remark-gfm for GitHub Flavored Markdown
 * (tables, strikethrough, task lists, etc.). No raw HTML is rendered
 * for security.
 */
export function Markdown({ id, props, style }: SDUIComponentProps) {
  const { content, maxHeight, className } = props as {
    content: string;
    maxHeight?: string;
    className?: string;
  };

  const containerStyle: CSSProperties = {
    ...(style as CSSProperties),
    ...(maxHeight ? { maxHeight, overflowY: 'auto' as const } : {}),
  };

  return (
    <div
      id={id}
      className={cn(
        'prose prose-sm max-w-none dark:prose-invert',
        'prose-headings:text-foreground prose-p:text-foreground',
        'prose-strong:text-foreground prose-em:text-foreground',
        'prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded',
        'prose-pre:bg-muted prose-pre:text-foreground',
        'prose-a:text-primary',
        'prose-li:text-foreground',
        className,
      )}
      style={containerStyle}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
    </div>
  );
}
